import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, EyeIcon, EyeOffIcon, FileTextIcon, LayoutGridIcon } from 'lucide-react';
import { RepPrompt } from './RepPrompt';
import { snakDisclaimer, snakMeta } from '../../data/snak';
import type { RepPromptContent } from '../../types/snak';
interface IpadFrameProps {
  step: number;
  total: number;
  chapter: string;
  children: React.ReactNode;
  prompt?: RepPromptContent;
  promptsVisible: boolean;
  onTogglePrompts: () => void;
  onOpenStudy: () => void;
  /** Internal, non-HCP-facing documents. */
  onOpenBrief: () => void;
  onOpenStoryboard: () => void;
  footnote?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Quiet escape hatch, e.g. skipping an unanswered question. */
  secondary?: {
    label: string;
    onClick: () => void;
  };
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function IpadFrame({
  step,
  total,
  chapter,
  children,
  prompt,
  promptsVisible,
  onTogglePrompts,
  onOpenStudy,
  onOpenBrief,
  onOpenStoryboard,
  footnote,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  secondary
}: IpadFrameProps) {
  const showPrompt = Boolean(prompt) && promptsVisible;
  const progress = (step + 1) / total;
  return <div className="flex min-h-full w-full items-center justify-center bg-mist px-6 py-10" data-id="element-97" data-mp="9s0rxvmpswb8">
      <div className="w-full max-w-[1080px]" data-id="element-98" data-mp="ogx5u9bgd3jr">
        <div className="rounded-[38px] bg-navy p-2.5 shadow-soft" data-id="element-99" data-mp="oflqrdck49dc">
          <div className="flex aspect-[4/3] w-full flex-col overflow-hidden rounded-[28px] bg-paper" data-id="element-100" data-mp="jj564t4kgd50">
            <div className="relative shrink-0 border-b border-line" data-id="element-101" data-mp="xh3f1j04bhd4">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-line/70" data-id="element-102" data-mp="tgt0hhy9fimn">
                <motion.div animate={{
                scaleX: progress
              }} initial={false} transition={{
                duration: 0.28,
                ease: EASE
              }} style={{
                transformOrigin: 'left'
              }} className="h-full bg-blue" data-id="element-103" data-mp="teoy54g0eroh" />
              </div>

              <div className="flex items-center justify-between gap-6 px-8 pb-4 pt-5" data-id="element-104" data-mp="136d5j2fjy5ji">
                <div className="flex items-baseline gap-3" data-id="element-105" data-mp="1386cyjxzotsk">
                  <span className="font-display text-[13px] font-bold tabular-nums text-blue" data-id="element-106" data-mp="184n2niku7g7r">
                    {String(step + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[11px] tabular-nums text-mut" data-id="element-107" data-mp="1h6wag0oq0vhg">
                    / {String(total).padStart(2, '0')}
                  </span>
                  <span className="ml-1 text-[10px] uppercase tracking-micro text-navy" data-id="element-108" data-mp="1m34kx0rz40ia">
                    {chapter}
                  </span>
                  <span className="hidden text-[10px] uppercase tracking-micro text-mut lg:inline" data-id="element-109" data-mp="1vvk5m5unn36v">
                    · {snakMeta.congress}
                  </span>
                </div>

                <div className="flex items-center gap-2" data-id="element-110" data-mp="24vfg08gdyu5u">
                  <button type="button" onClick={onTogglePrompts} aria-pressed={promptsVisible} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10.5px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${promptsVisible ? 'border-navy bg-navy text-paper' : 'border-line bg-paper text-mut hover:border-mut hover:text-navy'}`} data-id="element-111" data-mp="24vysvar6isd0">
                    {promptsVisible ? <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-112" data-mp="35zvlwkqnp0y" /> : <EyeOffIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-113" data-mp="3udcgalktyklm" />}
                    Rep prompts
                  </button>
                  <button type="button" onClick={onOpenStudy} className="rounded-full border border-blue/60 bg-paper px-3 py-1 text-[10.5px] font-medium text-navy transition-colors duration-150 hover:border-blue hover:bg-sky/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-114" data-mp="vox2sohnsr2g">
                    Study design
                  </button>
                </div>
              </div>
            </div>

            <div className="relative flex flex-1 flex-col overflow-hidden" data-id="element-115" data-mp="6f9jb4qn586z">{children}</div>

            <div className="shrink-0 border-t border-line bg-paper px-8 py-4" data-id="element-116" data-mp="u53t1dfnygn0">
              <div className="flex items-center justify-between gap-8" data-id="element-117" data-mp="z2nbyqnb3hla">
                {showPrompt && prompt ? <RepPrompt prompt={prompt} data-id="element-118" data-mp="ke976a6bxj23" /> : <p className="max-w-xl flex-1 text-[10px] leading-relaxed text-mut" data-id="element-119" data-mp="pd3p79wh33l5">
                    {footnote ?? snakMeta.principle}
                  </p>}

                {(onBack || onNext) && <div className="flex shrink-0 items-center gap-2" data-id="element-120" data-mp="1hvr5mc0ixufw">
                    {secondary && <button type="button" onClick={secondary.onClick} className="px-3 text-[12px] font-medium text-mut underline-offset-4 transition-colors duration-150 hover:text-navy hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-121" data-mp="32iqvbds1j3go">
                        {secondary.label}
                      </button>}
                    {onBack && <button type="button" onClick={onBack} aria-label="Previous page" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-mut hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-122" data-mp="2eo7e9tp6a80i">
                        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-123" data-mp="2wp20af1mca3l" />
                      </button>}
                    {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className="group flex h-12 min-w-[220px] items-center justify-between gap-3 rounded-full bg-navy pl-6 pr-2 text-[13px] font-medium text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:opacity-30" data-id="element-124" data-mp="31mbnrp5g6bos">
                        {nextLabel}
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-mint/20" data-id="element-125" data-mp="2rqicsa7dmq94">
                          <ArrowRightIcon className="h-4 w-4 text-mint transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-126" data-mp="1ubcv4i77ej6s" />
                        </span>
                      </button>}
                  </div>}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col items-center gap-4" data-id="element-127" data-mp="1hqnyqrcym3wp">
          <div className="flex items-center gap-2" data-id="element-128" data-mp="1dqx2470r4tk9">
            <span className="mr-1 text-[9px] uppercase tracking-micro text-mut" data-id="element-129" data-mp="1dq44pwnonges">Internal</span>
            <button type="button" onClick={onOpenBrief} className="flex h-9 items-center gap-2 rounded-full border border-line bg-paper px-4 text-[12px] font-medium text-navy transition-colors duration-150 hover:border-blue hover:bg-sky/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-130" data-mp="18sls9wagij21">
              <FileTextIcon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-131" data-mp="q12rlw3wnh36" />
              Strategic brief
            </button>
            <button type="button" onClick={onOpenStoryboard} className="flex h-9 items-center gap-2 rounded-full border border-line bg-paper px-4 text-[12px] font-medium text-navy transition-colors duration-150 hover:border-blue hover:bg-sky/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-132" data-mp="l286gnigtgfw">
              <LayoutGridIcon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-133" data-mp="23yjphjxxvo9a" />
              Storyboard
            </button>
          </div>

          <p className="mx-auto max-w-3xl text-center text-[10.5px] leading-relaxed text-mut" data-id="element-134" data-mp="1q5tqmexbaour">
            {snakDisclaimer}
          </p>
        </div>
      </div>
    </div>;
}